import { useState, useEffect } from 'react';
import { restaurantService } from '../services/restaurants';
import { useAuth } from '../hooks/useAuth';

interface Restaurant {
  id: string;
  name: string;
  description?: string;
  address?: string;
  owner_id?: string;
  timezone?: { name: string; offset: number };
}

interface RestaurantSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestaurantSelect: (restaurantId: string) => void;
}

export default function RestaurantSelectorModal({ 
  isOpen, 
  onClose, 
  onRestaurantSelect 
}: RestaurantSelectorModalProps) {
  const { isManager, user } = useAuth();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadRestaurants();
    }
  }, [isOpen]);

  const loadRestaurants = async () => {
    setLoading(true);
    try {
      // Solo managers necesitan seleccionar restaurantes para especificar el owner
      if (isManager()) {
        const restaurantsData = await restaurantService.getRestaurantsWithAccess(user?.uid || '', user?.email || '');
        setRestaurants(restaurantsData);
      }
    } catch (error) {
      console.error('Error loading restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (selectedRestaurant) {
      onRestaurantSelect(selectedRestaurant);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 relative">
        {/* Botón de cerrar */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Título */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Selecciona un Owner
          </h3>
          <p className="text-gray-600 text-sm">
            Como manager, debes seleccionar el restaurante para especificar a qué owner se aplicará la suscripción. 
            La suscripción se creará para el dueño del restaurante seleccionado.
          </p>
        </div>

        {/* Lista de restaurantes */}
        <div className="mb-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-500 mt-2">Cargando restaurantes...</p>
            </div>
          ) : restaurants.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No tienes restaurantes asignados.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {restaurants.map((restaurant) => (
                <div
                  key={restaurant.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedRestaurant === restaurant.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedRestaurant(restaurant.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{restaurant.name}</h4>
                      {restaurant.timezone?.name && (
                        <p className="text-xs text-[#22B6B2] mt-0.5">{restaurant.timezone.name}</p>
                      )}
                      {restaurant.description && (
                        <p className="text-sm text-gray-600 mt-1">{restaurant.description}</p>
                      )}
                      {restaurant.address && (
                        <p className="text-xs text-gray-500 mt-1">{restaurant.address}</p>
                      )}
                    </div>
                    {selectedRestaurant === restaurant.id && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botones */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 text-gray-500 hover:text-gray-700 font-medium py-3 px-4 rounded-xl border border-gray-300 transition-colors duration-200"
          >
            Cancelar
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedRestaurant}
            className={`flex-1 font-semibold py-3 px-4 rounded-xl transition-colors duration-200 ${
              selectedRestaurant
                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
} 